import type { ValidationIssue } from "~/components/csvImport/ValidationIssuesGrid";

import Papa from "papaparse";

import { CsvHeaderDiff, CsvParseResult } from "~/components/csvImport/fileUploadComponents/types";
import csvHeaderLabels from "~/helpers/csvImport/csvHeaderLabels";
import { csvRowNumberFromIndex } from "~/helpers/csvImport/csvImportHelpers";

// Node's TextDecoder does not apply Microsoft's 0x80-0x9F additions.
const WINDOWS_1252_HIGH_BYTES_TO_UNICODE: Record<number, number> = {
	0x80: 0x20ac,
	0x82: 0x201a,
	0x83: 0x0192,
	0x84: 0x201e,
	0x85: 0x2026,
	0x86: 0x2020,
	0x87: 0x2021,
	0x88: 0x02c6,
	0x89: 0x2030,
	0x8a: 0x0160,
	0x8b: 0x2039,
	0x8c: 0x0152,
	0x8e: 0x017d,
	0x91: 0x2018,
	0x92: 0x2019,
	0x93: 0x201c,
	0x94: 0x201d,
	0x95: 0x2022,
	0x96: 0x2013,
	0x97: 0x2014,
	0x98: 0x02dc,
	0x99: 0x2122,
	0x9a: 0x0161,
	0x9b: 0x203a,
	0x9c: 0x0153,
	0x9e: 0x017e,
	0x9f: 0x0178
};

const decodeWindows1252 = (bytes: Uint8Array): string => {
	let out = "";
	for (let i = 0; i < bytes.length; i++) {
		const b = bytes[i];
		out += String.fromCharCode(b < 0x80 || b > 0x9f ? b : (WINDOWS_1252_HIGH_BYTES_TO_UNICODE[b] ?? 0xfffd));
	}
	return out;
};

export const decodeFileAsUtf8WithWindows1252Fallback = async (file: File): Promise<string> => {
	const buffer = await file.arrayBuffer();
	try {
		return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
	} catch {
		return decodeWindows1252(new Uint8Array(buffer));
	}
};

const extraColumnIssue = (rowIndex: number, expectedHeaders: string[]): ValidationIssue => ({
	issue: `Too many columns — expected ${expectedHeaders.length}. Check for an unquoted comma in a value.`,
	rows: String(csvRowNumberFromIndex(rowIndex)),
	severity: "error",
	subject: "Columns"
});

const collectDuplicatedHeaders = (headers: string[]): string[] => {
	const seen = new Set<string>();
	const duplicates = new Set<string>();
	headers.forEach((header) => (seen.has(header) ? duplicates.add(header) : seen.add(header)));
	return [...duplicates];
};

export const collectHeaderDiff = (
	actualHeaders: string[],
	expectedHeaders: string[],
	requiredFields?: string[]
): CsvHeaderDiff => {
	const present = new Set(actualHeaders);
	const expected = new Set(expectedHeaders);
	const required = new Set(requiredFields ?? expectedHeaders);
	const missing = expectedHeaders.filter((header) => !present.has(header));

	return {
		duplicates: collectDuplicatedHeaders(actualHeaders),
		missingOptional: missing.filter((header) => !required.has(header)),
		missingRequired: missing.filter((header) => required.has(header)),
		unknown: [...new Set(actualHeaders.filter((header) => !expected.has(header)))]
	};
};

const collectHeaderErrors = (diff: CsvHeaderDiff): Array<{ message: string }> => {
	const messages = [
		...(diff.missingRequired.length > 0 ? [csvHeaderLabels.missingRequired(diff.missingRequired.join(", "))] : []),
		...(diff.duplicates.length > 0 ? [csvHeaderLabels.duplicates(diff.duplicates.join(", "))] : [])
	];

	return messages.length === 0 ? [] : [{ message: [...messages, csvHeaderLabels.templateHint()].join(" ") }];
};

const collectUnknownColumnIssues = (diff: CsvHeaderDiff): ValidationIssue[] =>
	diff.unknown.length === 0
		? []
		: [
				{
					issue: csvHeaderLabels.unknownIgnored(diff.unknown.join(", ")),
					rows: "1",
					severity: "warning",
					subject: "Columns"
				}
			];

export const nonEmptyExtraCells = (row: unknown): string[] => {
	const parsedExtraCells = (row as { __parsed_extra?: unknown[] }).__parsed_extra;
	if (!Array.isArray(parsedExtraCells)) return [];
	return parsedExtraCells.some((value) => String(value ?? "").trim() !== "")
		? parsedExtraCells.map((value) => String(value ?? ""))
		: [];
};

const hasNonEmptyExtraCells = (row: unknown): boolean => nonEmptyExtraCells(row).length > 0;

const collectExtraColumnIssues = <T>(rows: T[], expectedHeaders: string[]): ValidationIssue[] =>
	rows.flatMap((row, index) => (hasNonEmptyExtraCells(row) ? [extraColumnIssue(index, expectedHeaders)] : []));

const withoutUnknownColumns = <T>(rows: T[], unknownHeaders: string[]): T[] =>
	unknownHeaders.length === 0
		? rows
		: rows.map((row) => {
				const cleaned = { ...row } as Record<string, unknown>;
				unknownHeaders.forEach((header) => delete cleaned[header]);
				return cleaned as T;
			});

const collectStructuralParseErrors = <T>(results: Papa.ParseResult<T>): Papa.ParseError[] =>
	results.errors.filter((error) => error.code !== "TooFewFields" && error.code !== "TooManyFields");

const formatParseErrorMessage = (error: Papa.ParseError): { message: string } => ({
	message: error.row !== undefined && error.row > 0 ? `Row ${error.row + 1}: ${error.message}` : error.message
});

// Papa renames repeated headers (Genus, Genus_1) in meta.fields, so duplicates are only visible in the raw header row.
const readFileHeaders = (text: string): string[] => {
	const preview = Papa.parse<string[]>(text, {
		delimiter: ",",
		header: false,
		preview: 1,
		skipEmptyLines: "greedy"
	});
	return (preview.data[0] ?? []).map((header) => header.trim()).filter((header) => header !== "");
};

export interface CsvHeaderRow {
	cells: string[];
	linebreak: string;
	textAfterHeader: string;
}

export const readCsvHeaderRow = (text: string): CsvHeaderRow => {
	let headerRow: CsvHeaderRow = { cells: [], linebreak: "\n", textAfterHeader: "" };
	Papa.parse<string[]>(text, {
		delimiter: ",",
		skipEmptyLines: "greedy",
		step: ({ data, meta }, parser) => {
			headerRow = {
				cells: data.map((cell) => cell.trim()),
				linebreak: meta.linebreak,
				textAfterHeader: text.slice(meta.cursor)
			};
			parser.abort();
		}
	});
	return headerRow;
};

export type ExpectedCsvHeaders = string[] | ((actualHeaders: string[]) => string[]);

const resolveExpectedHeaders = (
	expectedHeaders: ExpectedCsvHeaders | undefined,
	actualHeaders: string[]
): string[] | undefined => (typeof expectedHeaders === "function" ? expectedHeaders(actualHeaders) : expectedHeaders);

export const parseCSV = async <T = Record<string, string>>(
	input: File | string,
	expectedHeaders?: ExpectedCsvHeaders,
	requiredFields?: string[]
): Promise<CsvParseResult<T>> => {
	const text = typeof input === "string" ? input : await decodeFileAsUtf8WithWindows1252Fallback(input);
	const fileHeaders = readFileHeaders(text);

	return new Promise((resolve, reject) => {
		const config = {
			complete: (results: Papa.ParseResult<T>) => {
				const rowKeys = results.meta.fields || [];
				const expected = resolveExpectedHeaders(expectedHeaders, fileHeaders);

				if (!expected || fileHeaders.length === 0) {
					resolve({
						data: results.data,
						errors: results.errors.map(formatParseErrorMessage),
						headers: rowKeys,
						rowIssues: []
					});
					return;
				}

				const headerDiff = collectHeaderDiff(fileHeaders, expected, requiredFields);
				const unknownRowKeys = rowKeys.filter((key) => !expected.includes(key));

				resolve({
					data: withoutUnknownColumns(results.data, unknownRowKeys),
					errors: [
						...collectStructuralParseErrors(results).map(formatParseErrorMessage),
						...collectHeaderErrors(headerDiff)
					],
					headerDiff,
					headers: fileHeaders,
					rowIssues: [...collectUnknownColumnIssues(headerDiff), ...collectExtraColumnIssues(results.data, expected)]
				});
			},
			delimiter: ",",
			dynamicTyping: false,
			error: (error: Error) => {
				reject(new Error(`CSV parsing failed: ${error.message}`));
			},
			header: true,
			skipEmptyLines: "greedy" as const,
			transformHeader: (header: string) => header.trim(),
			transform: (value: string) => value.trim()
		};

		Papa.parse(text, config);
	});
};

export const parseCSVRows = (text: string): string[][] => Papa.parse<string[]>(text, { skipEmptyLines: "greedy" }).data;

export const generateCSVContent = (headers: string[], rows: string[][]): string => {
	const data = [headers, ...rows];
	return Papa.unparse(data);
};

export const downloadBlob = (content: string, filename: string, mimeType: string): void => {
	const blob = new Blob([content], { type: mimeType });
	const link = document.createElement("a");
	const url = URL.createObjectURL(blob);

	link.setAttribute("href", url);
	link.setAttribute("download", filename);
	link.style.visibility = "hidden";

	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);

	URL.revokeObjectURL(url);
};

export const downloadCSV = (content: string, filename: string): void =>
	downloadBlob(content, filename, "text/csv;charset=utf-8;");

const FORMULA_TRIGGERS = new Set(["=", "+", "-", "@", "\t", "\r"]);

const sanitizeCsvCell = (value: unknown): string => {
	if (value == null) return "";
	let str = String(value);

	if (str.length > 0 && FORMULA_TRIGGERS.has(str[0])) {
		str = `'${str}`;
	}

	if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
		str = `"${str.replace(/"/g, '""')}"`;
	}

	return str;
};

export const escapeCsvFormulaTrigger = (value: unknown): string => {
	if (value == null) return "";
	const str = String(value);
	if (str.length > 0 && FORMULA_TRIGGERS.has(str[0])) return `'${str}`;
	return str;
};

const unescapeCsvFormulaTrigger = (value: string): string =>
	value.length >= 2 && value[0] === "'" && FORMULA_TRIGGERS.has(value[1]) ? value.slice(1) : value;

export default {
	downloadBlob,
	downloadCSV,
	escapeCsvFormulaTrigger,
	generateCSVContent,
	parseCSV,
	parseCSVRows,
	sanitizeCsvCell,
	unescapeCsvFormulaTrigger
};
